import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  IconButton,
  Tab,
  Tabs,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Block as BlockIcon,
  LockOpen as UnblockIcon,
  FilterList as FilterIcon,
  Videocam as JitsiIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import TrainingJitsiSettingsPanel from './TrainingJitsiSettingsPanel';

const STATUS_LABELS = {
  draft: 'Draft',
  ready_to_preview: 'Ready to preview',
  published: 'Published',
  archived: 'Archived',
  admin_blocked: 'Admin Blocked it'
};

const formatEdition = (n) => {
  if (n == null || Number(n) < 1) return 'Draft';
  return `V${String(Math.min(9999, Number(n))).padStart(4, '0')}`;
};

/**
 * General Administration → Training Administration
 * Catalog overview (list / tree), filter, admin block/unpublish + unblock.
 */
export default function TrainingAdministration() {
  const navigate = useNavigate();
  const [mainTab, setMainTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tree, setTree] = useState([]);
  const [viewMode, setViewMode] = useState('list');
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/training/admin/curriculum-tree', {
        params: {
          ...(q.trim() ? { q: q.trim() } : {}),
          ...(status ? { status } : {})
        }
      });
      setTree(r.data?.data?.tree || []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load training catalog');
      setTree([]);
    } finally {
      setLoading(false);
    }
  }, [q, status]);

  useEffect(() => {
    if (mainTab === 0) load();
  }, [load, mainTab]);

  const flatRows = useMemo(() => {
    const rows = [];
    tree.forEach((cat) => {
      (cat.spots || []).forEach((spot) => {
        (spot.editions || []).forEach((ed) => {
          rows.push({ cat, spot, ed });
        });
      });
    });
    return rows;
  }, [tree]);

  const blockEdition = async (ed) => {
    if (
      !window.confirm(
        `Unpublish and block "${ed.name}" (${formatEdition(ed.edition_number)})?\nInstructors will see status "Admin Blocked it" and a Helpdesk ticket will be logged.`
      )
    ) {
      return;
    }
    setBusyId(ed.id);
    try {
      const r = await api.post(`/training/admin/training-titles/${ed.id}/block`);
      toast.success(r.data?.message || 'Blocked');
      if (r.data?.data?.ticket_number) {
        toast.info(`Ticket ${r.data.data.ticket_number} created for unblock follow-up`);
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Block failed');
    } finally {
      setBusyId(null);
    }
  };

  const unblockEdition = async (ed) => {
    setBusyId(ed.id);
    try {
      const r = await api.post(`/training/admin/training-titles/${ed.id}/unblock`);
      toast.success(r.data?.message || 'Unblocked');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Unblock failed');
    } finally {
      setBusyId(null);
    }
  };

  const statusChip = (s) => (
    <Chip
      size="small"
      label={STATUS_LABELS[s] || s}
      color={s === 'published' ? 'success' : s === 'admin_blocked' ? 'error' : s === 'ready_to_preview' ? 'warning' : 'default'}
    />
  );

  const actionBtns = (ed) => (
    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
      {ed.status === 'admin_blocked' ? (
        <Tooltip title="Unblock">
          <span>
            <IconButton size="small" color="success" disabled={busyId === ed.id} onClick={() => unblockEdition(ed)}>
              <UnblockIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      ) : (
        <Tooltip title="Unpublish / Admin block">
          <span>
            <IconButton size="small" color="error" disabled={busyId === ed.id} onClick={() => blockEdition(ed)}>
              <BlockIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
      <Button
        size="small"
        onClick={() =>
          navigate(`/training/admin?tab=courses&categoryId=${ed.learning_spot_id ? '' : ''}`)
        }
        sx={{ display: 'none' }}
      >
        Open
      </Button>
    </Stack>
  );

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
        Training Administration
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Catalog block/unblock and Jitsi Meet recording policy for training live sessions.
      </Typography>

      <Tabs value={mainTab} onChange={(_, v) => setMainTab(v)} sx={{ mb: 2 }}>
        <Tab label="Catalog" />
        <Tab icon={<JitsiIcon />} iconPosition="start" label="Jitsi Settings" />
      </Tabs>

      {mainTab === 1 && <TrainingJitsiSettingsPanel />}

      {mainTab === 0 && (
        <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Review all categories, sub-categories, courses, and editions. Unpublish/block removes a course from the catalog and shows instructors{' '}
        <strong>Admin Blocked it</strong>; a Helpdesk ticket is logged to work with admin for unblock.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Stack direction="row" flexWrap="wrap" gap={2} alignItems="center">
          <FilterIcon color="action" />
          <TextField
            size="small"
            label="Filter"
            placeholder="Name, course code, edition…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            sx={{ minWidth: 220 }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {Object.keys(STATUS_LABELS).map((k) => (
                <MenuItem key={k} value={k}>
                  {STATUS_LABELS[k]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl component="fieldset">
            <RadioGroup row value={viewMode} onChange={(e) => setViewMode(e.target.value)}>
              <FormControlLabel value="list" control={<Radio size="small" />} label="List format" />
              <FormControlLabel value="tree" control={<Radio size="small" />} label="Tree format" />
            </RadioGroup>
          </FormControl>
          <Button variant="outlined" onClick={load}>
            Refresh
          </Button>
        </Stack>
      </Paper>

      {loading ? (
        <CircularProgress />
      ) : flatRows.length === 0 ? (
        <Alert severity="info">No courses match the current filters.</Alert>
      ) : viewMode === 'list' ? (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Category</TableCell>
                <TableCell>Sub-category</TableCell>
                <TableCell>Course</TableCell>
                <TableCell>Edition</TableCell>
                <TableCell>Course ID</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Admin</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {flatRows.map(({ cat, spot, ed }) => (
                <TableRow key={ed.id} hover>
                  <TableCell>
                    {cat.short_id ? `[${cat.short_id}] ` : ''}
                    {cat.name}
                  </TableCell>
                  <TableCell>
                    {spot.short_id ? `[${spot.short_id}] ` : ''}
                    {spot.name}
                  </TableCell>
                  <TableCell>{ed.name}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{formatEdition(ed.edition_number)}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{ed.course_code || '—'}</TableCell>
                  <TableCell>{statusChip(ed.status)}</TableCell>
                  <TableCell align="right">{actionBtns(ed)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Stack spacing={1}>
          {tree.map((cat) => (
            <Accordion key={cat.id} defaultExpanded disableGutters sx={{ border: '1px solid #ddd', borderRadius: '8px !important' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={700}>
                  {cat.short_id ? `[${cat.short_id}] ` : ''}
                  {cat.name}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {(cat.spots || []).map((spot) => (
                  <Box key={spot.id} sx={{ mb: 2, pl: 1, borderLeft: '3px solid #90CAF9' }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: '#1565C0' }}>
                      {spot.short_id ? `[${spot.short_id}] ` : ''}
                      {spot.name}
                    </Typography>
                    {(spot.editions || []).length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No editions
                      </Typography>
                    ) : (
                      <Table size="small">
                        <TableBody>
                          {(spot.editions || []).map((ed) => (
                            <TableRow key={ed.id}>
                              <TableCell>
                                {ed.name}{' '}
                                <Chip size="small" label={formatEdition(ed.edition_number)} sx={{ fontFamily: 'monospace', ml: 0.5 }} />
                              </TableCell>
                              <TableCell>{statusChip(ed.status)}</TableCell>
                              <TableCell align="right">{actionBtns(ed)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </Box>
                ))}
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      )}
        </>
      )}
    </Box>
  );
}
